import React, { useState } from "react";
import { useParams, useLoaderData } from "react-router-dom";
import { Stack, Button, Box, TextField, FormControlLabel, Checkbox, MenuItem, Select } from "@mui/material";
import { FullPagePaperLayout } from "../Layouts/FullPagePaperLayout";
import { PageBreadcrumbs, PageTitle, PageSubtitle } from "../Layouts/components/PageTitleContainer";
import { LocationManagementLocationType } from "../../types/locationManagement/locationType";
import { GOLD_LABEL_COLOR, LabelColor } from "../../types/locationManagement/labelColor";
import { FieldControl } from "components/Form/FieldControl";
import { Form } from "components/Form/Form";

const LocationTypeForm: React.FC = () => {
  const { locationTypeId } = useParams<{ locationTypeId?: string }>();
  const isEdit = Boolean(locationTypeId);
  const { locationType, labelColors } = useLoaderData() as {
    locationType: LocationManagementLocationType;
    labelColors: LabelColor[];
  };
  const gold = labelColors.find((color) => color.hex.toLowerCase() === GOLD_LABEL_COLOR.toLowerCase());
  const [labelColorId, setLabelColorId] = useState(
    String(locationType.labelColor?.id || locationType.labelColorId || gold?.id || ""),
  );

  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs links={[{ label: "Admin" }, { label: "Location Types", path: "/location-types" }]} />
      <PageTitle>{isEdit ? "Edit" : "Create"} Location Type</PageTitle>
      <PageSubtitle>
        Location type codes are often 4 characters and capitalized. Example: RESL, TECQ, PLLT. Exceptions include
        invoiced and ewasted. The label color is the band printed on that type&apos;s location labels. New types start
        on gold.
      </PageSubtitle>
      <Box sx={{ m: 3 }}>
        <Form method={isEdit ? "PUT" : "POST"} resource="locationType">
          <Stack direction="column" spacing={2}>
            <FieldControl
              name="name"
              label="Name"
              inputProps={{ defaultValue: locationType.name }}
              required
              component={TextField}
            />
            <FieldControl name="labelColorId" label="Label color">
              {({ label, labelId }) => (
                <>
                  <input type="hidden" name="location_type[label_color_id]" value={labelColorId} />
                  <Select
                    labelId={labelId}
                    label={label}
                    value={labelColorId}
                    onChange={(event) => setLabelColorId(event.target.value)}
                  >
                    {labelColors.map((color) => (
                      <MenuItem key={color.id} value={String(color.id)}>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            width: 14,
                            height: 14,
                            mr: 1,
                            bgcolor: color.hex,
                            border: "1px solid",
                            borderColor: "divider",
                            verticalAlign: "text-bottom",
                          }}
                        />
                        {color.name}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
            </FieldControl>
            <input type="hidden" name="location_type[allow_nested_locations]" value="false" />
            <FormControlLabel
              control={
                <Checkbox
                  name="location_type[allow_nested_locations]"
                  value="true"
                  defaultChecked={Boolean(locationType.allowNestedLocations)}
                />
              }
              label="Other locations can be placed inside locations of this type"
            />
            <div>
              <Button type="submit" variant="contained">
                {isEdit ? "Update" : "Create"}
              </Button>
            </div>
          </Stack>
        </Form>
      </Box>
    </FullPagePaperLayout>
  );
};

export default LocationTypeForm;
